import { withMutations, withResource } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createRxMutation, createRxResource } from '@next/core-lib/notification';
import { UserSelectors } from '@next/core-lib/user';
import { patchState, signalStore, withComputed, withHooks, withMethods } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';

import { AppState } from '@app/shared/models';
import { withCustomerParams } from '@app/shared/utils';

import { FmsSynchronizationStatusApiResponse } from './models/fms-sync.api-models';
import { FmsSyncStatusUpdatedEvent } from './models/fms-sync.events';
import { FmsSyncNotificationService } from './services/fms-sync-notification.service';
import { FmsSynchronizationState, FmsSynchronizationStatus } from './models/fms-sync.models';
import { FmsSyncService } from './services/fms-sync.service';

interface FmsSyncResourceParams {
  customerId: number;
}

const initialState: FmsSynchronizationState = {
  enabled: 'Uninitialized',
  status: 'None',
  startedAt: undefined,
  completedAt: undefined,
};

const mapSynchronizationStatus = (response: FmsSynchronizationStatusApiResponse): FmsSynchronizationState => ({
  enabled: response.isActive ? 'Enabled' : 'Disabled',
  status: response.status,
  startedAt: response.startedAt ? new Date(response.startedAt) : undefined,
  completedAt: response.completedAt ? new Date(response.completedAt) : undefined,
});

export const FmsSyncStore = signalStore(
  { providedIn: 'root' },
  withResource(
    () => {
      const appStore = inject(Store<AppState>);
      const fmsSyncService = inject(FmsSyncService);
      const customer = appStore.selectSignal(UserSelectors.selectCustomer);

      return {
        synchronizationStatus: createRxResource<FmsSynchronizationState, FmsSyncResourceParams | undefined>({
          params: () => withCustomerParams(customer()?.id, (customerId) => ({ customerId })),
          stream: ({ params }) =>
            fmsSyncService.getSynchronizationStatus(params.customerId).pipe(map(mapSynchronizationStatus)),
          defaultValue: initialState,
          errorMessage: 'fmsSync.statusFailed',
        }),
      };
    },
    { errorHandling: 'previous value' },
  ),
  withMutations((store, appStore = inject(Store<AppState>)) => {
    const fmsSyncService = inject(FmsSyncService);
    const customer = appStore.selectSignal(UserSelectors.selectCustomer);

    return {
      triggerSync: createRxMutation<void, void>({
        operation: () => fmsSyncService.triggerSync(customer()?.id),
        onSuccess: () => store._synchronizationStatusReload(),
        notification: {
          successMessage: 'fmsSync.triggered',
          errorMessage: 'fmsSync.failed',
        },
      }),
    };
  }),
  withComputed((store) => ({
    status: computed(() => store.synchronizationStatusValue().status),
    startedAt: computed(() => store.synchronizationStatusValue().startedAt),
    completedAt: computed(() => store.synchronizationStatusValue().completedAt),
    isEnabled: computed(() => store.synchronizationStatusValue().enabled === 'Enabled'),
    isSynchronizing: computed(() => ['Initiated', 'Syncing'].includes(store.synchronizationStatusValue().status)),
  })),
  withMethods((store, appStore = inject(Store<AppState>)) => {
    const customer = appStore.selectSignal(UserSelectors.selectCustomer);

    const updateSynchronizationStatus = (event: FmsSyncStatusUpdatedEvent, status: FmsSynchronizationStatus): void => {
      if (event.CustomerId !== customer()?.id) {
        return;
      }

      const synchronizationStatus = store.synchronizationStatusValue();
      let timestampUpdate: Partial<Pick<FmsSynchronizationState, 'startedAt' | 'completedAt'>>;

      if (status === 'Initiated') {
        timestampUpdate = { startedAt: undefined, completedAt: undefined };
      } else if (status === 'Syncing') {
        timestampUpdate = { startedAt: new Date(event.TimeStamp), completedAt: undefined };
      } else {
        timestampUpdate = { completedAt: new Date(event.TimeStamp) };
      }

      patchState(store, {
        synchronizationStatusValue: {
          ...synchronizationStatus,
          status,
          ...timestampUpdate,
        },
      });
    };

    return {
      setInitiated(event: FmsSyncStatusUpdatedEvent): void {
        updateSynchronizationStatus(event, 'Initiated');
      },
      setSyncing(event: FmsSyncStatusUpdatedEvent): void {
        updateSynchronizationStatus(event, 'Syncing');
      },
      setSynced(event: FmsSyncStatusUpdatedEvent): void {
        updateSynchronizationStatus(event, 'Synced');
      },
      setFailedToSync(event: FmsSyncStatusUpdatedEvent): void {
        updateSynchronizationStatus(event, 'Failed');
      },
      setNothingToSync(event: FmsSyncStatusUpdatedEvent): void {
        updateSynchronizationStatus(event, 'NothingToSync');
      },
    };
  }),
  withHooks({
    onInit(store): void {
      const notificationService = inject(FmsSyncNotificationService);

      notificationService.onInitiated.pipe(takeUntilDestroyed()).subscribe((event) => store.setInitiated(event));

      notificationService.onSyncing.pipe(takeUntilDestroyed()).subscribe((event) => store.setSyncing(event));

      notificationService.onSynced.pipe(takeUntilDestroyed()).subscribe((event) => store.setSynced(event));

      notificationService.onFailed.pipe(takeUntilDestroyed()).subscribe((event) => store.setFailedToSync(event));

      notificationService.onNothingToSync
        .pipe(takeUntilDestroyed())
        .subscribe((event) => store.setNothingToSync(event));
    },
  }),
);
